import { MappedEntity, PreliminaryPrice, SnakeMeta } from '@api';
import { PriceDisplay } from '@design/widget-modules/price-display/PriceDisplay';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { LivePriceInfo } from '../../hooks/use-live-price';
import { globalPriceFormatter } from '../../utils/asset-price/assetPrice';
import { formatPriceDisplay } from './helpers/price-display-helpers';
import styles from './PriceDisplayView.module.scss';

export interface PriceDisplayViewProps {
  entity: MappedEntity;
  suffix?: string;
  livePriceInfo?: LivePriceInfo;
  snakeMeta?: SnakeMeta;
  preliminaryPrice?: PreliminaryPrice;
  isChartHovered: boolean;
  isCondensed?: boolean;
}

export const PriceDisplayView = ({
  entity,
  livePriceInfo,
  snakeMeta,
  suffix,
  preliminaryPrice,
  isChartHovered,
  isCondensed = false,
}: PriceDisplayViewProps) => {
  const { t } = useTranslation(['analyze']);

  const priceFormatter = useCallback(
    (price: number) =>
      globalPriceFormatter({
        entity,
        price,
        priceSuffix: suffix ?? '',
        shouldShowCurrency: false,
      }),
    [suffix, entity]
  );

  if (
    (!livePriceInfo && snakeMeta?.last_value === undefined) ||
    !snakeMeta?.last_timestamp
  ) {
    return null;
  }

  const price = formatPriceDisplay({
    exchange: entity.exchange.code,
    livePriceInfo,
    preliminaryPrice,
    meta: snakeMeta,
    isHovered: isChartHovered,
    t,
  });

  const hasLivePrice =
    price.secondary?.price !== undefined && !!price.secondary?.isLive;
  const isPrimaryPriceCondensed = isCondensed && hasLivePrice;

  return (
    <div className={styles.container} data-testid="price-display-view">
      {price.primary.price !== undefined && !isPrimaryPriceCondensed && (
        <PriceDisplay
          className={styles.styledPriceDisplay}
          price={priceFormatter(price.primary.price)}
          currency={entity.currency}
          priceChange={price.primary.priceChange}
          liveLabel={
            price.primary?.isLive ? t('analyze:analysis.livePrice') : undefined
          }
          timeNode={
            !price.primary.isLive && (
              <span className={styles.dateSpan}>
                {price.primary.asOfDate
                  ? t('analyze:analysis.asOfDate', {
                      date: price.primary.asOfDate,
                    })
                  : t('analyze:analysis.asOfLastClose')}
              </span>
            )
          }
          showMarketIndicator={!isCondensed}
        />
      )}
      {!isChartHovered &&
        price.secondary !== undefined &&
        !!price.secondary.price &&
        !!price.secondary.isLive && (
          <PriceDisplay
            className={styles.styledPriceDisplay}
            data-testid="secondary-price"
            price={priceFormatter(price.secondary.price)}
            currency={entity.currency}
            priceChange={price.secondary.priceChange}
            liveLabel={t('analyze:analysis.livePrice')}
            outsideHoursText={price.secondary.outsideHours}
            isPrimary={false}
            showMarketIndicator={!isCondensed}
          />
        )}
    </div>
  );
};
